import { defineStore } from 'pinia';
import { useSocketStore } from './socket';

export interface ServerStore {
    // The server store is primarily a container for the socket store
    // Additional server-specific state can be added here if needed
    initialized?: boolean;
    // Whether the split SSH console pane is open on the server console page.
    // Lives here (not on the page) because the toggle sits in the breadcrumb
    // actions bar, which is rendered by the layout.
    sshConsoleOpen: boolean;
}

export const useServerStore = defineStore('server', {
    state: (): ServerStore => ({
        initialized: false,
        sshConsoleOpen: false,
    }),

    getters: {
        // Proxy socket store getters for backward compatibility
        socket: () => useSocketStore(),

        // Direct access to socket state
        isConnected: () => useSocketStore().isConnected,
        hasError: () => useSocketStore().hasError,
        connectionError: () => useSocketStore().connectionError,
        serverStatus: () => useSocketStore().serverStatus,
        serverStats: () => useSocketStore().serverStats,
        gameStats: () => useSocketStore().gameStats,

        // Convenience getters
        isServerOnline: () => useSocketStore().isServerOnline,
        isServerOffline: () => useSocketStore().isServerOffline,
        connectionStatus: () => useSocketStore().connectionStatus,
        serverStatusText: () => useSocketStore().serverStatusText,

        // Resource usage
        resourceUsage: () => useSocketStore().resourceUsage,
        cpuUsage: () => useSocketStore().cpuUsage,
        memoryUsage: () => useSocketStore().memoryUsage,
        diskUsage: () => useSocketStore().diskUsage,

        // Game info
        gameInfo: () => useSocketStore().gameInfo,
        currentPlayers: () => useSocketStore().currentPlayers,
        maxPlayers: () => useSocketStore().maxPlayers,
    },

    actions: {
        // Proxy socket store actions for backward compatibility
        setState(connected: boolean) {
            const socketStore = useSocketStore();
            socketStore.setState(connected);
        },

        setError(error?: string) {
            const socketStore = useSocketStore();
            socketStore.setError(error);
        },

        setStatus(status: any) {
            const socketStore = useSocketStore();
            socketStore.setStatus(status);
        },

        setProc(proc: any) {
            const socketStore = useSocketStore();
            socketStore.setProc(proc);
        },

        setQuery(query: any) {
            const socketStore = useSocketStore();
            socketStore.setQuery(query);
        },

        // Enhanced actions
        connect() {
            const socketStore = useSocketStore();
            socketStore.connect();
        },

        disconnect(error?: string) {
            const socketStore = useSocketStore();
            socketStore.disconnect(error);
        },

        updateServerData(data: any) {
            const socketStore = useSocketStore();
            socketStore.updateServerData(data);
        },

        toggleSshConsole() {
            this.sshConsoleOpen = !this.sshConsoleOpen;
        },

        reset() {
            const socketStore = useSocketStore();
            socketStore.reset();
            this.sshConsoleOpen = false;
        },
    },
});
